import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { ApWindowService } from '@ap/cdk';
import { ApHeadlessContentService } from '@ap/content';
import { ContractDetails, MijnOverzichtResource } from '@ap/interface-apix-mijn-overzicht';
import { MijnNavigatieService } from '@ap/mijn-navigatie';

import { RESET_ROUTE_KEYS } from './mijn-overzicht-template/mijn-overzicht-template.constant';
import { HeadlessContent } from './mijn-template.model';

export const XC_HEADLESS_CONTENT = '@ap/mijn-template-content';

@Injectable({ providedIn: 'root' })
export class MijnTemplateService {
    private headlessContent: HeadlessContent[] = [];

    public constructor(
        private readonly apHeadlessContentService: ApHeadlessContentService,
        private readonly mijnOverzichtResource: MijnOverzichtResource,
        private readonly windowService: ApWindowService,
        private readonly mijnNavigatieService: MijnNavigatieService
    ) {}

    public getContractDetails(): Observable<ContractDetails> {
        return this.mijnOverzichtResource.getContractDetails().pipe(catchError(() => of(null)));
    }

    public static mapHeadlessContentItems(headlessContent?: { [key: string]: string }, isBusiness = false): { [key: string]: string } {
        const mappedContent: { [key: string]: string } = {};
        for (const key in headlessContent) {
            if (headlessContent.hasOwnProperty(key) && !key.startsWith('business.')) {
                let keyContent = headlessContent[key];
                if (isBusiness && headlessContent.hasOwnProperty(`business.${key}`)) {
                    keyContent = headlessContent[`business.${key}`];
                }
                mappedContent[key] = keyContent
                    .replace(/&amp;/gm, '&')
                    .replace(/&lt;/gm, '<')
                    .replace(/&gt;/gm, '>')
                    .replace(/&#\d+;/gm, (s) => String.fromCharCode(parseInt(s.match(/\d+/gm)[0], 10)));
            }
        }
        return mappedContent;
    }

    public getHeadlessContent(content = XC_HEADLESS_CONTENT, isBusiness = false): Observable<HeadlessContent> {
        if (this.headlessContent[content] !== undefined) {
            return of(this.headlessContent[content]);
        }

        return this.apHeadlessContentService.getContentItems(content).pipe(
            map((result) => MijnTemplateService.mapHeadlessContentItems(result, isBusiness)),
            tap((result) => {
                if (result) {
                    this.headlessContent[content] = result;
                }
            })
        );
    }

    public storeUrl(url?: string, title?: string): void {
        const storageUrl = url?.length > 0 ? url : (this.windowService.location_href() as string);
        const isResetUrl = MijnTemplateService.isResetUrl(storageUrl);
        this.mijnNavigatieService.storeUrl(storageUrl, isResetUrl, title);
    }

    private static isResetUrl(url: string): boolean {
        const routeKeyForUrl = MijnNavigatieService.getRouteKeyForUrl(url);
        return RESET_ROUTE_KEYS.includes(routeKeyForUrl);
    }
}
